import React, { useState } from "react";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Handshake, Lock, EyeSlash, ShareNetwork, Warning } from "@phosphor-icons/react";
import WhyThisMatters from "../WhyThisMatters";
import { api } from "../../lib/api";
import { toast } from "sonner";

const OPTIONS = [
  {
    value: "private",
    icon: Lock,
    title: "Keep case private",
    body: "Your case is visible only to you. No attorneys can see it.",
  },
  {
    value: "anonymized",
    icon: EyeSlash,
    title: "Share anonymized summary",
    body: "Attorneys see case type, state, and a redacted summary (party names removed). They must request an intro to see full details.",
  },
  {
    value: "full",
    icon: ShareNetwork,
    title: "Share full case",
    body: "Attorneys see the full case when an intro is requested and you accept. Party names are still redacted in the browse listing.",
  },
];

export default function ShareTab({ caseData, save, reload }) {
  const [level, setLevel] = useState(caseData.sharing_level || "private");
  const [outcome, setOutcome] = useState(caseData.marketplace_desired_outcome || caseData.questionnaire?.relief_requested || "");
  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put(`/cases/${caseData.case_id}/sharing`, {
        sharing_level: level,
        marketplace_desired_outcome: outcome,
      });
      toast.success("Sharing preferences saved");
      reload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="font-serif-display text-3xl text-slate-900">Get Attorney Interest <span className="text-slate-500 text-lg">(Optional)</span></h2>
          <p className="text-slate-600 mt-1 max-w-3xl">
            You can share a summary of your case with attorneys who may be interested in reviewing it. You are not required to do this to use the app.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className="label-tiny">Step 7 of 8</span>
          <Handshake weight="duotone" className="w-10 h-10 text-slate-700" />
        </div>
      </div>

      <RadioGroup value={level} onValueChange={setLevel} data-testid="sharing-level-radio">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
          {OPTIONS.map((opt, i) => {
            const Icon = opt.icon;
            const selected = level === opt.value;
            return (
              <label
                key={opt.value}
                htmlFor={`sharing-${opt.value}`}
                className={`cursor-pointer border border-slate-200 ${i > 0 ? "md:border-l-0" : ""} -mt-px md:mt-0 p-5 transition-colors ${selected ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50"}`}
                data-testid={`sharing-option-${opt.value}`}
              >
                <div className="flex items-start gap-3">
                  <RadioGroupItem id={`sharing-${opt.value}`} value={opt.value} className={selected ? "border-white" : ""} />
                  <Icon weight="duotone" className={`w-6 h-6 shrink-0 ${selected ? "text-white" : "text-slate-700"}`} />
                  <div className="flex-1">
                    <div className={`font-serif-display text-xl leading-tight ${selected ? "text-white" : "text-slate-900"}`}>{opt.title}</div>
                    <p className={`text-sm mt-2 ${selected ? "text-slate-200" : "text-slate-600"}`}>{opt.body}</p>
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </RadioGroup>

      {level !== "private" && (
        <div className="border border-slate-200 p-6 space-y-3">
          <Label className="label-tiny">Desired Outcome (shown to attorneys)</Label>
          <Textarea
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            placeholder="e.g., Compensatory damages of approx. $50,000, plus costs and fees."
            className="rounded-none border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 min-h-[100px]"
            data-testid="desired-outcome-textarea"
          />
          <p className="text-xs text-slate-500">Tip: keep this high-level. Attorneys see the case type, state, a redacted summary, and this outcome before requesting an intro.</p>
        </div>
      )}

      <div className="border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 space-y-2" data-testid="marketplace-disclaimers">
        <div className="flex items-start gap-2">
          <Warning weight="duotone" className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Attorney availability notice</p>
            <p className="mt-1">
              Attorneys may choose not to respond or take a case for various reasons, including availability, practice focus,
              case details, or fees. This platform does not guarantee attorney interest or representation.
            </p>
          </div>
        </div>
        <ul className="list-disc list-inside pl-7 text-amber-900">
          <li>No attorney–client relationship is created</li>
          <li>No guarantee of representation or outcome</li>
          <li>Attorneys may pay for access to case information</li>
        </ul>
      </div>

      <div className="flex justify-end pt-2 border-t border-slate-200">
        <Button onClick={onSave} disabled={saving} className="rounded-none bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-sharing-btn">
          {saving ? "Saving…" : "Save sharing preferences"}
        </Button>
      </div>

      <WhyThisMatters step="share" />
    </div>
  );
}
